import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import PageSeo from "@/components/PageSeo";
import TrustedBy from "@/components/sections/TrustedBy";
import Home from "@/pages/Home";

/**
 * Service landing pages for SEO — one data-driven template, five services.
 * Routes: /services/{slug}   (see SERVICES keys)
 * Schema: Service + FAQPage per page (BreadcrumbList via PageSeo).
 */
export const SERVICES = {
  "brand-film-production-bangalore": {
    name: "Brand Film Production",
    h1a: "Brand film production",
    h1b: "in Bengaluru",
    eyebrow: "services · brand films",
    metaTitle: "Brand Film Production in Bangalore · Relatable Studio",
    metaDescription:
      "Story-first brand films for founders, launches and category-defining moments. 1,600+ projects, 300+ brands. Bengaluru production house — get a quote and a treatment direction within one working day.",
    tagline: "A brand film isn't an ad with better music. It's the one asset that outlives the campaign.",
    intro:
      "We build brand films the way documentaries get made — research first, camera later. Real people, real rooms, a script that sounds like your founder actually talks. Shot by our Bengaluru crew, shipped as a hero film plus every cutdown your media plan needs.",
    facts: [
      { k: "₹4–6L", v: "starting budget" },
      { k: "₹12–35L", v: "typical hero film" },
      { k: "4–6 weeks", v: "kickoff to master" },
    ],
    deliverables: [
      { t: "Hero brand film", d: "3–8 minute story-led film for your site, launch event, and sales deck." },
      { t: "Social cutdowns", d: "60s / 30s / 15s edits recut natively for Instagram, LinkedIn and YouTube pre-roll." },
      { t: "Stills + BTS", d: "Production stills and behind-the-scenes coverage from every shoot day." },
      { t: "Master + raw archive", d: "Colour-graded 4K master, captioned versions, and archived raw footage." },
    ],
    process: [
      { n: "01", t: "Listen", d: "A working session with your founders and marketing team. We leave with the one thing the film must say." },
      { n: "02", t: "Treat", d: "A written treatment — narrative spine, visual references, locations, budget bands. You approve before a camera is booked." },
      { n: "03", t: "Shoot", d: "1–4 shoot days with our Bengaluru crew. Interview-led or fully scripted, your call." },
      { n: "04", t: "Cut", d: "Two structured review rounds. We show you the film early and ugly, then early and beautiful." },
      { n: "05", t: "Ship", d: "Colour, sound design, titles, captions, cutdowns. Masters delivered, raw archived for 12 months." },
    ],
    faqs: [
      { q: "How much does a brand film cost in Bangalore?", a: "A one-day, one-location interview-led brand film starts at ₹4–6 lakhs end-to-end (crew, camera, edit, delivery). A multi-day hero brand film with a director-DP combo, colour, sound design and social cutdowns typically lands between ₹12 and ₹35 lakhs. We share a full pricing PDF on request via /contact." },
      { q: "How long does a brand film take to produce?", a: "Count 4–6 weeks from kickoff to master delivery: one week of pre-production, 2–3 shoot days, 2–3 weeks of edit, one week of colour, sound and revisions. Treatment-led campaign films can run 8–12 weeks." },
      { q: "Do we need a locked script before we come to you?", a: "No. About 60% of our brand films start from a one-page brief — we research, script and direct. If your agency has a locked script, we're happy to execute it, but we'll flag anything we think won't survive the edit." },
      { q: "What makes a brand film different from a corporate video?", a: "A corporate video explains; a brand film persuades. Brand films are built around a narrative — a founder's obsession, a customer's turning point — and are designed to be watched voluntarily, not endured in a lobby loop." },
      { q: "Can you shoot outside Bengaluru?", a: "Yes — roughly 40% of our shoots happen across India. Bengaluru is home base, which keeps pre-production and post costs low; travel and local crew are quoted separately and transparently." },
    ],
    related: ["corporate-film-production-bangalore", "startup-video-production-bangalore"],
  },

  "corporate-film-production-bangalore": {
    name: "Corporate Film Production",
    h1a: "Corporate film production",
    h1b: "in Bengaluru",
    eyebrow: "services · corporate films",
    metaTitle: "Corporate Film Production in Bangalore · Relatable Studio",
    metaDescription:
      "Corporate films, AGM films, milestone films and event aftermovies that people actually watch. Bengaluru production house serving TCS, Coca-Cola, Adani and 300+ brands.",
    tagline: "The corporate film is where good stories go to die. We've spent six years fixing that.",
    intro:
      "Milestone films, leadership messages, AGM openers, factory walk-throughs, event aftermovies — the formats everyone dreads. We make them worth watching by treating your company like a subject, not a client: find the tension, find the humans, cut everything else.",
    facts: [
      { k: "₹3–5L", v: "starting budget" },
      { k: "₹8–20L", v: "typical multi-day" },
      { k: "3–5 weeks", v: "kickoff to master" },
    ],
    deliverables: [
      { t: "Corporate / milestone film", d: "4–10 minute film for anniversaries, AGMs, investor days and internal launches." },
      { t: "Event aftermovie", d: "Multi-cam coverage with a 2–3 minute wrap film delivered within a week." },
      { t: "Leadership messages", d: "Teleprompter-ready or interview-led CXO films that don't feel like hostage videos." },
      { t: "Facility & process films", d: "Factory, campus and lab walk-throughs with licensed music and motion graphics." },
    ],
    process: [
      { n: "01", t: "Brief", d: "We map stakeholders, approval chains and the actual audience — employees, investors, or both." },
      { n: "02", t: "Script", d: "A voiceover script or interview framework your leadership can sign off in one pass." },
      { n: "03", t: "Shoot", d: "Multi-location days planned around your operations — we've shot in cleanrooms, foundries and trading floors." },
      { n: "04", t: "Edit", d: "Structured review rounds with a single consolidated-feedback rule that keeps timelines honest." },
      { n: "05", t: "Deliver", d: "Master film plus internal-comms cutdowns, captioned versions and stills." },
    ],
    faqs: [
      { q: "What does a corporate film cost in Bangalore?", a: "A single-day corporate film starts around ₹3–5 lakhs including crew, edit and delivery. Multi-location milestone films with motion graphics and licensed music typically run ₹8–20 lakhs. Event aftermovies start at ₹2.5 lakhs for single-day coverage." },
      { q: "How fast can you deliver an event aftermovie?", a: "Standard delivery is 5–7 working days after the event. If you need a same-week or next-morning highlight cut for social, we can put an editor on site — tell us at briefing stage." },
      { q: "Can you work within our brand and legal guidelines?", a: "Yes. We regularly deliver under enterprise brand books, logo-clearance rules, and legal review cycles — TCS, Coca-Cola and Adani among them. Send the guidelines with the brief and we build them into the treatment." },
      { q: "Do you handle permissions for factory or campus shoots?", a: "We handle the production side: shot lists, crew NDAs, safety compliance, drone permits where legal. Internal facility access approvals stay with your team, and we give you exactly what to ask for." },
      { q: "Our last corporate film was boring. How is yours different?", a: "Because we don't start with a shot of the building. We find the human story inside the milestone — the engineer who stayed 20 years, the first invoice, the night shift — and build the film around that. Corporate facts land only after someone cares." },
    ],
    related: ["brand-film-production-bangalore", "employer-branding-films-bangalore"],
  },

  "employer-branding-films-bangalore": {
    name: "Employer Branding Films",
    h1a: "Employer branding films",
    h1b: "in Bengaluru",
    eyebrow: "services · employer branding",
    metaTitle: "Employer Branding Films in Bangalore · Relatable Studio",
    metaDescription:
      "Culture films and recruitment videos with real teams, real work, no stock footage. Fill your hiring pipeline with films candidates actually finish watching. Bengaluru production house.",
    tagline: "Candidates don't believe your careers page. They believe your people, on camera, unscripted.",
    intro:
      "An employer branding film works when a candidate watches it and thinks 'I can see myself there.' That never comes from stock footage and scripted testimonials. We spend time inside your teams, find the people who light up talking about their work, and film the truth — well-lit.",
    facts: [
      { k: "₹3.5–6L", v: "starting budget" },
      { k: "₹8–15L", v: "typical series" },
      { k: "3–5 weeks", v: "kickoff to master" },
    ],
    deliverables: [
      { t: "Culture film", d: "3–5 minute anchor film for your careers page and offer-letter emails." },
      { t: "Recruiter cutdowns", d: "30–60s role-specific and team-specific edits for LinkedIn and job posts." },
      { t: "Employee story series", d: "2–3 minute individual stories that humanise engineering, ops and sales teams." },
      { t: "Careers page hero loop", d: "A silent 15–30s loop cut specifically for your careers page header." },
    ],
    process: [
      { n: "01", t: "Immerse", d: "We sit in your office before we bring a camera — standups, canteens, the Friday demos. That's where the film is." },
      { n: "02", t: "Cast", d: "We interview 10–15 employees off-camera and shortlist the storytellers, not the seniors." },
      { n: "03", t: "Shoot", d: "1–3 documentary-style days. No scripts, no marks — questions that get people past the rehearsed answers." },
      { n: "04", t: "Cut", d: "Edit for honesty first, polish second. HR and legal review built into the schedule." },
      { n: "05", t: "Deploy", d: "Masters plus a rollout kit — LinkedIn versions, job-post embeds, recruiter signatures." },
    ],
    faqs: [
      { q: "What does an employer branding film cost?", a: "A single culture film starts at ₹3.5–6 lakhs. A full package — culture film plus an employee story series and recruiter cutdowns — typically runs ₹8–15 lakhs. Per-film cost drops meaningfully when shoots are combined." },
      { q: "Our employees aren't actors. Will they look awkward?", a: "That's the point — and our job. We interview off-camera first, shoot conversations rather than statements, and never hand anyone a script. The awkward ones don't make the cut; the genuine ones carry the film." },
      { q: "How do you measure whether the film worked?", a: "Watch-through rate on the careers page, application conversion before/after embed, and recruiter response rates when the film is attached to outreach. We help you set up the baseline before launch." },
      { q: "Can you make versions for specific roles or teams?", a: "Yes — that's the recruiter cutdown deliverable. From one shoot we cut role-specific 30–60s versions (engineering, sales, design) so recruiters aren't sending a generic film to a specialist candidate." },
      { q: "How much of our employees' time do you need?", a: "About 30 minutes per interviewed employee for the off-camera conversation, then 60–90 minutes on shoot day for those selected. We schedule around standups and releases — your sprint survives." },
    ],
    related: ["corporate-film-production-bangalore", "startup-video-production-bangalore"],
  },

  "product-video-production-bangalore": {
    name: "Product Video Production",
    h1a: "Product video production",
    h1b: "in Bengaluru",
    eyebrow: "services · product videos",
    metaTitle: "Product Video & Explainer Production in Bangalore · Relatable Studio",
    metaDescription:
      "Product films, SaaS explainers and D2C launch videos that translate feature lists into feelings. Bengaluru production house for product teams that hate generic explainers.",
    tagline: "Nobody buys a feature list. They buy the moment the product makes their day easier.",
    intro:
      "SaaS demos, D2C launch films, app walkthroughs, hardware reveals — we make product videos that start with the user's problem, not your interface. Live-action, screencast, motion graphics or all three, depending on what your product actually needs to be understood.",
    facts: [
      { k: "₹2.5–4L", v: "starting budget" },
      { k: "₹6–15L", v: "typical launch film" },
      { k: "2–4 weeks", v: "kickoff to master" },
    ],
    deliverables: [
      { t: "60–90s explainer", d: "The anchor asset for your homepage, app store page and paid campaigns." },
      { t: "Feature cutdowns", d: "15–30s single-feature edits for retargeting and product-launch drips." },
      { t: "Demo film", d: "2–4 minute guided walkthrough for sales calls and onboarding emails." },
      { t: "Launch kit", d: "Vertical versions, GIF loops, thumbnail stills and captioned masters." },
    ],
    process: [
      { n: "01", t: "Use it", d: "We onboard as a real user first. The friction we hit becomes the script's opening scene." },
      { n: "02", t: "Script", d: "Problem → moment of relief → proof. One page, timed to the second, approved before production." },
      { n: "03", t: "Produce", d: "Live-action shoot, UI capture at 4K, or motion design — usually a blend. Voice-over casting included." },
      { n: "04", t: "Cut", d: "Edit against the script timing. Two review rounds with your product and growth teams." },
      { n: "05", t: "Version", d: "Masters plus every aspect ratio and length your channels need, named and organised." },
    ],
    faqs: [
      { q: "What does a product explainer video cost in Bangalore?", a: "A screencast-plus-motion-graphics explainer starts at ₹2.5–4 lakhs. Live-action product films with actors, locations and art direction typically run ₹6–15 lakhs. Hardware and D2C launch films vary with art-direction ambition — we quote from a one-page brief." },
      { q: "Live-action or animation — which should we pick?", a: "If trust is the barrier (fintech, health, B2B), show real humans. If the product is abstract (APIs, infrastructure), motion graphics explain faster. Most of our product films blend both: a human problem in live-action, the product moment in crisp UI capture." },
      { q: "Can you match our product's design system?", a: "Yes. We build motion-graphics templates from your actual design tokens — typography, colour, radii, easing — so the film looks like your product team made it, not a vendor." },
      { q: "How do you handle products that are still in beta?", a: "Common for launch films. We shoot around locked flows, use staging environments with seeded data, and keep UI shots modular so a changed screen is a patch, not a re-edit." },
      { q: "Do you write the voice-over and cast the voice?", a: "Both. Script and VO direction are part of every product film. We shortlist 3–5 voices (Indian and neutral-international accents) and record with direction — you approve the voice before the session." },
    ],
    related: ["startup-video-production-bangalore", "brand-film-production-bangalore"],
  },

  "startup-video-production-bangalore": {
    name: "Startup Video Production",
    h1a: "Startup video production",
    h1b: "in Bengaluru",
    eyebrow: "services · startup films",
    metaTitle: "Startup Video Production in Bangalore · Relatable Studio",
    metaDescription:
      "Founder films, fundraise videos and launch films for startups — built lean, priced honestly. Bengaluru production house that has filmed 40+ funded startups.",
    tagline: "Your deck says what you do. A founder film shows why anyone should believe you'll pull it off.",
    intro:
      "We've filmed startups from pre-seed garages to post-IPO campuses. Founder films for fundraises, launch films for Product Hunt and D-day, culture films for the first 50 hires. Lean crews, honest pricing, and a bias for shipping fast — because your runway is real.",
    facts: [
      { k: "₹1.5–3L", v: "founder film package" },
      { k: "₹4–10L", v: "typical launch film" },
      { k: "2–4 weeks", v: "kickoff to master" },
    ],
    deliverables: [
      { t: "Founder film", d: "2–4 minute interview-led film — the story, the obsession, the why-now." },
      { t: "Fundraise / investor cut", d: "A tighter 90s version built to sit inside your data room and cold emails." },
      { t: "Launch film", d: "High-energy product or brand launch film for Product Hunt, X and LinkedIn." },
      { t: "Social kit", d: "Vertical cutdowns, quote cards and stills for the launch-week content push." },
    ],
    process: [
      { n: "01", t: "Coffee", d: "A 45-minute conversation with the founders. We're listening for the story you tell when the pitch deck is closed." },
      { n: "02", t: "Frame", d: "A one-page treatment: the narrative, the visual approach, and exactly what it costs. No surprise line items." },
      { n: "03", t: "Shoot", d: "Usually one lean day — 3-person crew, your office or a location we source. Founders lose half a day, not a week." },
      { n: "04", t: "Cut", d: "First cut in 7–10 days. Two review rounds, then colour and sound." },
      { n: "05", t: "Launch", d: "Masters plus the social kit, timed to your launch date. We've never missed one." },
    ],
    faqs: [
      { q: "We're pre-seed. What's the cheapest film you'd recommend?", a: "The lean-crew founder film package: one location, one shoot day, a 2–3 minute film plus a 60s cutdown — ₹1.5–3 lakhs all-in. If your budget is under that, we'll say so honestly and suggest what to do with a phone and a good mic until the next round." },
      { q: "How fast can you deliver for a launch date?", a: "Standard is 2–4 weeks kickoff to master. For a hard launch date we can compress to 10–12 days with a locked brief and same-day approvals. Tell us the date on the first call — we build the schedule backwards from it." },
      { q: "Do investors actually watch founder films?", a: "The 90s investor cut, yes — especially attached to a warm intro or in the data room. It answers the question a deck can't: what is this founder like in a room? Several of our clients cite the film in their fundraise post-mortems." },
      { q: "Can we reuse the footage later?", a: "Yes, and you should. We shoot founder films with extra coverage so the same material can be recut for hiring, product launches and social. Raw footage is archived for 12 months and handed over on request." },
      { q: "What do you need from us before the shoot?", a: "Three things: 45 minutes with the founders, a locked date, and access to your space (or a note that we should source a location). We handle everything else — questions, crew, kit, music licensing, captions." },
    ],
    related: ["brand-film-production-bangalore", "product-video-production-bangalore"],
  },
};

function buildServiceLd(slug, s) {
  const url = `https://relatable.studio/services/${slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        "name": `${s.name} in Bangalore`,
        "serviceType": s.name,
        "url": url,
        "description": s.metaDescription,
        "areaServed": [
          { "@type": "City", "name": "Bengaluru" },
          { "@type": "Country", "name": "India" },
        ],
        "provider": {
          "@type": ["LocalBusiness", "ProfessionalService"],
          "@id": "https://relatable.studio/production/bangalore#business",
          "name": "Relatable Studio",
          "url": "https://relatable.studio",
          "telephone": "+91-93800-21206",
          "email": "nexa@relatable.studio",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "BHIVE Platinum, Church Street Campus",
            "addressLocality": "Bengaluru",
            "addressRegion": "Karnataka",
            "postalCode": "560001",
            "addressCountry": "IN",
          },
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": `${s.name} deliverables`,
          "itemListElement": s.deliverables.map((d) => ({
            "@type": "Offer",
            "itemOffered": { "@type": "Service", "name": d.t, "description": d.d },
          })),
        },
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        "mainEntity": s.faqs.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
    ],
  };
}

export default function ServiceLanding() {
  const { slug } = useParams();
  const s = SERVICES[slug];
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects?featured=true").then((r) => {
      setProjects((r.data || []).slice(0, 3));
    }).catch(() => {});
  }, []);

  if (!s) return <Home />;

  return (
    <main className="bg-ink text-bone">
      <PageSeo
        title={s.metaTitle}
        description={s.metaDescription}
        path={`/services/${slug}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Services", path: `/services/${slug}` },
          { name: s.name, path: `/services/${slug}` },
        ]}
        jsonLd={buildServiceLd(slug, s)}
      />

      {/* HERO */}
      <section
        data-testid="service-hero"
        className="relative pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ {s.eyebrow}</p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95] max-w-5xl"
        >
          {s.h1a}
          <br />
          <span className="text-fog/60">{s.h1b.split(" ")[0]} </span>
          <span className="text-relatable">{s.h1b.split(" ").slice(1).join(" ")}.</span>
        </motion.h1>
        <p className="mt-8 md:mt-10 font-display text-xl md:text-2xl text-bone/75 leading-snug max-w-3xl">
          {s.tagline}
        </p>
        <p className="mt-6 text-base md:text-lg text-fog/85 leading-relaxed max-w-3xl">
          {s.intro}
        </p>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-10 max-w-3xl">
          {s.facts.map((f) => (
            <div key={f.v} data-testid={`service-fact-${f.v.replace(/\s+/g, "-").toLowerCase()}`}>
              <p className="font-display text-3xl md:text-4xl tracking-tight font-medium">{f.k}</p>
              <p className="mt-2 eyebrow text-fog text-[10px] md:text-xs tracking-[0.22em]">{f.v}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Link
            to={`/contact?utm_source=service-${slug}&utm_medium=hero-cta&utm_campaign=service-landing`}
            data-testid="service-hero-cta"
            className="group inline-flex items-center gap-3 bg-bone text-ink px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:bg-relatable hover:text-bone transition-colors"
          >
            Get a quote
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
          <a
            href="tel:+919380021206"
            data-testid="service-hero-call"
            className="inline-flex items-center gap-3 border border-line text-bone px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:border-bone transition-colors"
          >
            Call the studio · +91 93800 21206
          </a>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section
        data-testid="service-deliverables"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ what you get</p>
        <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98] max-w-4xl">
          Everything ships together.<br />
          <span className="text-fog/60">Nothing is an add-on.</span>
        </h2>
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {s.deliverables.map((d, i) => (
            <div key={d.t} data-testid={`service-deliverable-${i}`} className="border-t border-line pt-6">
              <p className="font-mono text-xs text-fog tracking-[0.22em]">0{i + 1} / 0{s.deliverables.length}</p>
              <h3 className="mt-4 font-display text-2xl md:text-3xl tracking-tight font-medium">{d.t}</h3>
              <p className="mt-3 text-base text-bone/75 leading-relaxed max-w-lg">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section
        data-testid="service-process"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line bg-graphite/50"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <p className="eyebrow text-fog text-xs tracking-[0.28em]">◇ how it works</p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[0.98]">
              Five steps.<br />
              <span className="text-relatable">Zero surprises.</span>
            </h2>
          </div>
          <div className="md:col-span-2 space-y-8">
            {s.process.map((p) => (
              <div key={p.n} data-testid={`service-process-${p.n}`} className="flex gap-6 border-t border-line pt-6">
                <span className="font-mono text-xs text-fog tracking-[0.28em] shrink-0">{p.n}</span>
                <div>
                  <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium">{p.t}</h3>
                  <p className="mt-2 text-base text-bone/75 leading-relaxed max-w-xl">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustedBy />

      {/* FEATURED WORK */}
      {projects.length > 0 && (
        <section
          data-testid="service-work"
          className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
        >
          <div className="flex items-end justify-between gap-6 mb-10 md:mb-14">
            <div>
              <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ proof of work</p>
              <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98] max-w-3xl">
                Watch before<br />
                <span className="text-fog/60">you brief.</span>
              </h2>
            </div>
            <Link to="/work" className="hidden md:inline-flex items-center gap-2 eyebrow text-bone link-line whitespace-nowrap pb-2">
              All work <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {projects.map((p) => (
              <Link key={p.slug || p.id} to={`/work/${p.slug}`} data-testid={`service-work-${p.slug}`} className="group block">
                <div className="aspect-[4/3] bg-graphite overflow-hidden">
                  {p.thumbnail_url && (
                    <img
                      src={p.thumbnail_url}
                      alt={p.title || "Project"}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <p className="mt-4 font-display text-lg tracking-tight font-medium group-hover:text-relatable transition-colors">
                  {p.title}
                </p>
                <p className="mt-1 eyebrow text-fog text-[10px] tracking-[0.22em]">{p.category || p.division}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section
        data-testid="service-faq"
        className="py-20 md:py-28 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <p className="eyebrow text-fog text-xs tracking-[0.28em]">◇ everyone asks</p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tighter font-medium leading-[0.98]">
              Straight answers,<br />
              <span className="text-fog/60">before the call.</span>
            </h2>
          </div>
          <div className="md:col-span-2 space-y-8">
            {s.faqs.map((f, i) => (
              <details key={f.q} data-testid={`service-faq-${i}`} className="group border-t border-line pt-6 open:pb-2">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <span className="font-display text-lg md:text-2xl tracking-tight font-medium leading-snug">{f.q}</span>
                  <span className="font-mono text-xs text-fog/70 tracking-[0.22em] shrink-0 pt-2 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-base text-bone/75 leading-relaxed max-w-2xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section
        data-testid="service-related"
        className="py-16 md:py-20 px-6 md:px-12 lg:px-24 border-b border-line"
      >
        <p className="eyebrow text-fog text-xs tracking-[0.28em]">◇ also from the studio</p>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {s.related.map((r) => (
            <Link
              key={r}
              to={`/services/${r}`}
              data-testid={`service-related-${r}`}
              className="group border border-line p-6 hover:border-bone transition-colors"
            >
              <p className="font-display text-xl md:text-2xl tracking-tight font-medium group-hover:text-relatable transition-colors">
                {SERVICES[r].name}
              </p>
              <p className="mt-2 font-mono text-xs text-fog tracking-wider">→ /services/{r}</p>
            </Link>
          ))}
          <Link
            to="/production/bangalore"
            data-testid="service-related-city"
            className="group border border-line p-6 hover:border-bone transition-colors"
          >
            <p className="font-display text-xl md:text-2xl tracking-tight font-medium group-hover:text-relatable transition-colors">
              Production in Bengaluru
            </p>
            <p className="mt-2 font-mono text-xs text-fog tracking-wider">→ /production/bangalore</p>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="service-cta" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl">
          <p className="eyebrow text-fog text-xs md:text-sm tracking-[0.28em]">◇ next step</p>
          <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
            Brief us on<br />
            <span className="text-relatable">your {s.name.split(" ")[0].toLowerCase()} film.</span>
          </h2>
          <p className="mt-8 font-display text-xl md:text-2xl text-bone/75 max-w-2xl">
            Tell us what you&apos;re trying to say. We&apos;ll come back within one working day with a treatment direction and a ballpark budget.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to={`/contact?utm_source=service-${slug}&utm_medium=footer-cta&utm_campaign=service-landing`}
              data-testid="service-footer-cta"
              className="group inline-flex items-center gap-3 bg-bone text-ink px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:bg-relatable hover:text-bone transition-colors"
            >
              Brief the studio
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <a
              href="mailto:nexa@relatable.studio?subject=Project%20enquiry"
              data-testid="service-footer-email"
              className="inline-flex items-center gap-3 border border-line text-bone px-6 py-3 md:px-8 md:py-4 font-mono text-sm tracking-wider hover:border-bone transition-colors"
            >
              nexa@relatable.studio
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
